import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Required for refresh token cookies
})

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add cache-busting headers for active run endpoint to prevent stale data
    if (config.url && config.url.includes('/scavenging/run/active')) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
      config.headers['Pragma'] = 'no-cache'
      config.headers['Expires'] = '0'
      // Add timestamp query parameter for additional cache-busting
      const separator = config.url.includes('?') ? '&' : '?'
      config.url = `${config.url}${separator}_t=${Date.now()}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Token refresh state
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Routes that establish, renew, or end a session. A 401 on these must NOT kick
// off another refresh: /auth/refresh would loop, and login/callback/logout
// failures are real auth failures rather than a lapsed access token.
// Everything else — including /auth/me, which bootstraps the app — is renewable
// and has to try the silent refresh before giving up.
const NON_RENEWABLE_AUTH_ROUTES = [
  '/auth/login',
  '/auth/callback',
  '/auth/refresh',
  '/auth/logout',
  '/auth/register',
  '/auth/verify',
  '/auth/forgot-password',
  '/auth/reset-password',
]

const isNonRenewableAuthRoute = (url) =>
  NON_RENEWABLE_AUTH_ROUTES.some((route) => url?.includes(route))

// Read the `exp` claim of a stored JWT. Client-side hint for proactive refresh
// only — never a trust decision (the server always re-validates).
export function getTokenExpiry(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(payload))
    return typeof decoded.exp === 'number' ? decoded.exp : null
  } catch {
    return null
  }
}

export function isAccessTokenExpired(token, skewSeconds = 5) {
  const exp = getTokenExpiry(token)
  if (!token || !exp) return false
  return exp * 1000 <= Date.now() + skewSeconds * 1000
}

// Rotate the session using the httpOnly refresh cookie; returns the new access token.
export async function refreshAccessToken() {
  const response = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true }
  )
  const { access_token } = response.data
  localStorage.setItem('access_token', access_token)
  return access_token
}

const redirectToLogin = () => {
  if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/auth/callback')) {
    window.location.href = '/'
  }
}

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const isUnauthorized = error.response?.status === 401
    const nonRenewable = isNonRenewableAuthRoute(originalRequest?.url)

    if (isUnauthorized && !originalRequest._retry && !nonRenewable) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const access_token = await refreshAccessToken()

        // Process queued requests
        processQueue(null, access_token)

        // Replay the request that triggered the refresh with the new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed (missing / revoked / expired cookie) - the session is over
        processQueue(refreshError, null)
        localStorage.removeItem('access_token')

        console.error('Token refresh failed:', {
          message: refreshError.response?.data?.detail || 'Session expired',
          status: refreshError.response?.status
        })

        redirectToLogin()

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Non-renewable auth endpoints that fail with 401: just clear and redirect
    if (isUnauthorized && nonRenewable) {
      localStorage.removeItem('access_token')
      redirectToLogin()
    }

    return Promise.reject(error)
  }
)

// Player API methods
export const getPlayerState = () => api.get('/player/state')
export const getDailyRewardState = () => api.get('/player/daily-rewards')
export const claimDailyReward = () => api.post('/player/daily-rewards/claim')
export const getEffectDefinition = (effectKey) => api.get(`/player/effects/${effectKey}`)

// Catch-up digest (offline catch-up) — consumed server-side on fetch
export const getCatchUpDigest = () => api.get('/player/catch-up')

// Presence heartbeat — advances the catch-up anchor so the digest only
// replays events missed since the player was last actually observed.
export const markPlayerSeen = () => api.post('/player/seen')
// Page-hide beacon. XHR (axios) dies with the page, so this uses fetch
// keepalive directly; sendBeacon cannot attach the Authorization header.
export const markPlayerSeenBeacon = () => {
  const token = localStorage.getItem('access_token')
  if (!token) return
  try {
    fetch(`${api.defaults.baseURL}/player/seen`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {})  // best effort by design — never surface unload noise
  } catch {
    // Page is unloading; nothing to do.
  }
}

// Events log (management view) — durable per-player record of every event
export const getEventsLog = ({ category, eventType, limit = 50, skip = 0 } = {}) =>
  api.get('/events-log', {
    params: {
      ...(category ? { category } : {}),
      ...(eventType ? { event_type: eventType } : {}),
      limit,
      skip,
    },
  })

// Achievements API methods
export const claimAchievementRewards = (achievementId) => api.post(`/achievements/${achievementId}/claim-rewards`)

// Skills API methods
export const getSkills = () => api.get('/skills')
export const getSkill = (skillKey) => api.get(`/skills/${skillKey}`)
export const getSkillDefinitions = () => api.get('/skills/definitions')

// Gatherings API methods
export const getGatherings = () => api.get('/gathering/')
export const getGatheringDefinition = (gatheringId) => api.get(`/gathering/${gatheringId}`)
export const getActiveGathering = () => api.get('/gathering/active')
export const startGathering = (gatheringId) => api.post(`/gathering/start/${gatheringId}`)
export const stopGathering = () => api.post('/gathering/stop')
export const collectGatheringLoot = () => api.post('/gathering/collect-loot')

// Vendors API methods
export const getVendors = () => api.get('/vendors')
export const getVendor = (vendorId) => api.get(`/vendors/${vendorId}`)
export const getVendorInventory = (vendorId) => api.get(`/vendors/${vendorId}/inventory`)
export const buyFromVendor = (vendorId, itemId, quantity) =>
  api.post(`/vendors/${vendorId}/buy`, { item_id: itemId, quantity })
export const getVendorQuests = (vendorId) => api.get(`/vendors/${vendorId}/quests`)
export const acceptQuest = (vendorId, questId) =>
  api.post(`/vendors/${vendorId}/quests/${questId}/accept`)
export const turnInQuest = (vendorId, questId) =>
  api.post(`/vendors/${vendorId}/quests/${questId}/turn-in`)
export const getActiveQuests = () => api.get('/vendors/quests/active')

// Market API methods
export const getMarketListings = (filters = {}) => {
  const params = new URLSearchParams()
  if (filters.item_type) params.append('item_type', filters.item_type)
  if (filters.variant) params.append('variant', filters.variant)
  if (filters.min_price !== undefined && filters.min_price !== '') params.append('min_price', filters.min_price)
  if (filters.max_price !== undefined && filters.max_price !== '') params.append('max_price', filters.max_price)
  if (filters.search) params.append('search', filters.search)
  if (filters.sort_by) params.append('sort_by', filters.sort_by)
  if (filters.sort_order) params.append('sort_order', filters.sort_order)
  if (filters.limit) params.append('limit', filters.limit)
  if (filters.offset) params.append('offset', filters.offset)
  const queryStr = params.toString()
  return api.get(`/market/listings${queryStr ? '?' + queryStr : ''}`)
}
export const getMyListings = (status) =>
  api.get('/market/my-listings', { params: { status } })
export const listItem = (playerItemId, price) =>
  api.post('/market/list', { player_item_id: playerItemId, price })
export const buyItem = (listingId) =>
  api.post(`/market/buy/${listingId}`)
export const collectCurrency = (listingId) =>
  api.post(`/market/collect-currency/${listingId}`)
export const collectItem = (listingId) =>
  api.post(`/market/collect-item/${listingId}`)
export const cancelListing = (listingId) =>
  api.delete(`/market/cancel/${listingId}`)

// Rest / Sleep API (rooms service)
export const getRestOptions = () => api.get('/rest/options')
export const getRestStatus = () => api.get('/rest/status')
export const startRest = (mode, hours) => api.post('/rest/start', { mode, hours })

// The Well API (rooms service)
export const getWellStatus = () => api.get('/well/status')
export const drawWell = () => api.post('/well/use')

// Codex API methods
export const getCodexStatus = () => api.get('/codex/status')
export const getCodexUpgradeCosts = () => api.get('/codex/upgrade-cost')
export const getItemUpgradeRequirements = (itemId) => api.get(`/codex/item/${itemId}/requirements`)
export const getEnemyUpgradeRequirements = (enemyName) => api.get(`/codex/enemy/${encodeURIComponent(enemyName)}/requirements`)
export const upgradeItemCodex = (itemId) => api.post('/codex/item/upgrade', { item_id: itemId })
export const upgradeEnemyCodex = (enemyName) => api.post('/codex/enemy/upgrade', { enemy_name: enemyName })

// Handle & Profile API methods
export const checkHandleAvailability = (handle) =>
  api.get('/player/handle/available', { params: { handle } })
export const setInitialHandle = (handle) =>
  api.post('/player/handle/set-initial', { handle })
export const updatePlayerHandle = (handle) =>
  api.put('/player/handle', { handle })
export const getHandleInfo = () =>
  api.get('/player/handle/me')
export const getPlayerProfile = (handle) =>
  api.get(`/player/profile/${handle}`)
export const searchPlayers = (query, limit = 10) =>
  api.get('/player/search', { params: { query, limit } })

// Admin API methods
export const adminTelemetryOverview = () => api.get('/admin/telemetry/overview')
export const adminTelemetryPlayers = ({ search, page, pageSize = 25 } = {}) =>
  api.get('/admin/telemetry/players', { params: { search: search || undefined, page, page_size: pageSize } })
export const adminTelemetryGameplay = (limit = 10) =>
  api.get('/admin/telemetry/gameplay', { params: { limit } })
export const adminItems = () => api.get('/admin/items')
export const adminPlayerDetail = (playerId) => api.get(`/admin/players/${playerId}`)
export const adminGrantItem = (playerId, itemId, quantity) =>
  api.post(`/admin/players/${playerId}/items/grant`, { item_id: itemId, quantity })
export const adminRemoveItem = (playerId, lootId) =>
  api.post(`/admin/players/${playerId}/items/remove`, { loot_id: lootId })
export const adminRepairAll = (playerId) =>
  api.post(`/admin/players/${playerId}/items/repair-all`)
export const adminAdjustCurrency = (playerId, delta) =>
  api.post(`/admin/players/${playerId}/currency`, { delta })
export const adminAdjustLevels = (playerId, levels) =>
  api.post(`/admin/players/${playerId}/experience`, { levels })
export const adminRestoreStats = (playerId) =>
  api.post(`/admin/players/${playerId}/stats/restore`)
export const adminClearAction = (playerId) =>
  api.post(`/admin/players/${playerId}/action/clear`)
export const adminExpandInventory = (playerId, width, height) =>
  api.post(`/admin/players/${playerId}/inventory/expand`, { width, height })
export const adminResetPlayer = (playerId) =>
  api.post(`/admin/players/${playerId}/reset`)
export const adminBlockUser = (userId, reason) =>
  api.post(`/admin/users/${userId}/block`, { reason })
export const adminUnblockUser = (userId) =>
  api.post(`/admin/users/${userId}/unblock`)
export const adminUserDetail = (userId) => api.get(`/admin/users/${userId}`)

// Web Push (PWA notifications) — notifications service
export const getVapidPublicKey = () => api.get('/push/vapid-public-key')
export const subscribePush = (subscription) =>
  api.post('/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: subscription.keys,
    user_agent: subscription.user_agent,
  })
export const unsubscribePush = (endpoint) => api.post('/push/unsubscribe', { endpoint })

export default api

